import {
  MailOutlined,
  MinusOutlined,
  PaperClipOutlined,
  PhoneOutlined,
  PlusOutlined,
  ReloadOutlined,
  SendOutlined,
  UserOutlined,
} from "@ant-design/icons"
import {
  App,
  Button,
  Checkbox,
  Divider,
  Popconfirm,
  Select,
  Space,
  Spin,
  Tree,
  Upload,
} from "antd"
import { compact, groupBy, sortBy, uniq } from "lodash"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import styled from "styled-components"

import CollapseIcon from "#assets/collapseIcon.svg?react"
import ExpandIcon from "#assets/expandIcon.svg?react"
import MakeupsIcon from "#assets/makeupsIcon.svg?react"
import PlannedAbsencesIcon from "#assets/plannedAbsencesIcon.svg?react"
import StudentsIcon from "#assets/studentsIcon.svg?react"
import TryoutsIcon from "#assets/tryoutsIcon.svg?react"
import { classSessionAtoms, storyTimeAtoms } from "#atoms"
import ButtonToggleModal from "#blocks/ButtonToggleModal"
import Flex from "#blocks/Flex"
import Firebase from "#classes/Firebase"
import Firestore from "#classes/Firestore"
import { EXCEPTION_LIST_VIEWS } from "#constants/exceptionListConstants"
import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { useApiContext } from "#contexts/ApiContextProvider"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { useUserContext } from "#contexts/UserContextProvider"
import { RESOURCES, USER_ACTIONS } from "#customTypes/composedTypes/authTypes"
import {
  ClassSessionConstructor,
  type StoryTimeRecording,
} from "#customTypes/composedTypes/classSessionTypes"
import {
  EMAIL_RECIPIENT_TYPE,
  EmailAttachment,
  EmailRecipient,
} from "#customTypes/composedTypes/emailTypes"
import {
  COMPARATORS,
  DOC_TYPES,
} from "#customTypes/composedTypes/firebaseTypes"
import { type StoryTimeConstructor } from "#customTypes/composedTypes/storyTimeTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { Uuid } from "#customTypes/globalTypes"
import { AttendanceStatus, invariant } from "#customTypes/types"
import { useFilteredDocs, useOneDoc } from "#hooks/jotai/useSubscription"
import { AdminViewClassSession } from "#hooks/newHooks/useAdminViewData"
import useExceptionListData from "#hooks/newHooks/useExceptionListData"
import useHasPermission from "#hooks/newHooks/useHasPermission"
import { getSessionInfoFromId } from "#utils/classHelpers"
import {
  buildStoryTimeRecorder,
  buildStoryTimeRecording,
  buildStoryTimeStoragePath,
  emailStoryTimeStudentsOnce,
  encodedAttachmentsOverLimit,
  guardianEmailsForStoryTimeSend,
  isStoryTimeShared,
  listStoryTimeRecordings,
  planSharedStoryTimeSends,
  STORYTIME_EMAIL_BASE64_LIMIT,
  storyTimeMessageBase64Bytes,
  storyTimeRecordingsPatch,
  storyTimeSendFailureMessage,
} from "#utils/storyTimeHelpers"

import type { UploadFile } from "antd"
import type { Key } from "antd/es/table/interface"

const DEFAULT_EXPANDED_KEYS: Key[] = [
  "enrolled",
  "makeups",
  "tryouts",
  "assistants",
  "plannedAbsences",
] as const

const EMAIL_KEY_SUFFIXES = [
  "-guardian1Email",
  "-guardian2Email",
  "-personalEmail",
] as const

const PHONE_KEY_SUFFIXES = [
  "-guardian1Phone",
  "-guardian2Phone",
  "-personalPhone",
] as const

const CONTACT_KEY_SUFFIXES: string[] = [
  ...EMAIL_KEY_SUFFIXES,
  ...PHONE_KEY_SUFFIXES,
]

const isEmailKey = (key: string) =>
  EMAIL_KEY_SUFFIXES.some((suffix) => key.endsWith(suffix))

const isPhoneKey = (key: string) =>
  PHONE_KEY_SUFFIXES.some((suffix) => key.endsWith(suffix))

const isContactKey = (key: string) =>
  CONTACT_KEY_SUFFIXES.some((suffix) => key.endsWith(suffix))

const formatMb = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(1)}MB`

const urlToEmailAttachment = async (
  url: string,
  filename: string
): Promise<EmailAttachment> => {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`Could not download ${filename}`)
  }
  const blob = await response.blob()
  const type = blob.type || "audio/mp4"
  const file = new File([blob], filename || "story-time.m4a", { type })
  return fileToEmailAttachment(file)
}

const fileToEmailAttachment = (file: File): Promise<EmailAttachment> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      // readAsDataURL yields "data:<mime>;base64,<content>"
      const content = String(reader.result).split(",")[1] ?? ""
      resolve({
        type: file.type || "application/octet-stream",
        name: file.name,
        content,
      })
    }
    reader.onerror = () =>
      reject(reader.error ?? new Error(`Failed to read ${file.name}`))
    reader.readAsDataURL(file)
  })

type ClassSessionContactInfo = {
  contactInfoByStudentId: Record<
    Uuid,
    {
      emails: string[]
      phones: string[]
    }
  >
  studentIdsByType: Record<
    "makeups" | "tryouts" | "plannedAbsences" | "assistants" | "students",
    Uuid[]
  >
  session: ClassSessionConstructor | null
  studentsById: Record<Uuid, StudentConstructor>
  isReady: boolean
}

const useCopyClassContactInfoData = (
  sessionId: Uuid
): ClassSessionContactInfo => {
  const session = useOneDoc<ClassSessionConstructor>(
    classSessionAtoms,
    DOC_TYPES.CLASS_SESSIONS,
    sessionId
  )
  const adminViewClassSessions = useMemo(() => {
    if (!session) return []

    return [
      {
        id: session?.id,
        session: session,
      },
    ] as AdminViewClassSession[]
  }, [session])

  const { studentsById, studentsByExceptionType, isReady } =
    useExceptionListData(adminViewClassSessions)

  const contactInfoByStudentId = useMemo(() => {
    const result: Record<
      Uuid,
      {
        emails: string[]
        phones: string[]
      }
    > = {}

    Object.values(studentsById).forEach((student: StudentConstructor) => {
      const emails = compact([
        student.personalEmail,
        student.guardian1Email,
        student.guardian2Email,
      ])
      const phones = compact([
        student.personalPhone,
        student.guardian1Phone,
        student.guardian2Phone,
      ])

      if (emails.length > 0 || phones.length > 0) {
        result[student.id] = {
          emails: uniq(emails),
          phones: uniq(phones),
        }
      }
    })

    return result
  }, [studentsById])

  const studentIdsByType = useMemo(() => {
    return {
      makeups: uniq(
        studentsByExceptionType[EXCEPTION_LIST_VIEWS.makeups].map(
          (item) => item.student.id
        )
      ),
      tryouts: uniq(
        studentsByExceptionType[EXCEPTION_LIST_VIEWS.tryouts].map(
          (item) => item.student.id
        )
      ),
      plannedAbsences: uniq(
        studentsByExceptionType[EXCEPTION_LIST_VIEWS.plannedAbsences].map(
          (item) => item.student.id
        )
      ),
      assistants: uniq(
        studentsByExceptionType[EXCEPTION_LIST_VIEWS.assistants].map(
          (item) => item.student.id
        )
      ),
      students: (session?.studentIds ?? []) as Uuid[],
    }
  }, [studentsByExceptionType, session])

  return {
    contactInfoByStudentId,
    studentIdsByType,
    session,
    studentsById,
    isReady,
  }
}

const getAbsentStudentIds = (
  session: ClassSessionConstructor,
  studentsById: Record<Uuid, StudentConstructor>
): Uuid[] => {
  const [classId, sessionIndex] = getSessionInfoFromId(session.id)
  const absentIds: Uuid[] = []

  session.studentIds?.forEach((studentId) => {
    const student = studentsById[studentId]
    if (!student) return

    const status =
      student.attendance?.[classId]?.[sessionIndex]?.status ??
      (ATTENDANCE_STATUSES.ABSENT as AttendanceStatus)

    if (status === ATTENDANCE_STATUSES.ABSENT || status.includes("ABSENT")) {
      absentIds.push(studentId)
    }
  })

  return absentIds
}

const AddRemoveButtonContainer = styled(Flex)`
  border: 1px solid #ccc;
  padding: 5px;
  width: fit-content;
  border-radius: 5px;
  align-items: center;
  justify-content: center;
  gap: 5px;
  width: 150px;
`

const AddRemoveButton = ({
  label,
  onAdd,
  onRemove,
  icon,
  color,
}: {
  label: string
  onAdd: () => void
  onRemove: () => void
  icon?: React.ReactNode
  color?: string
}) => {
  return (
    <AddRemoveButtonContainer>
      <Button
        size="small"
        icon={<MinusOutlined />}
        onClick={onRemove}
        shape="circle"
      />
      <Flex gap={5} grow>
        <Flex
          center
          style={{
            fontWeight: 500,
            width: "100%",
            color: color,
            gap: 6,
          }}
        >
          {icon}
          {label}
        </Flex>
        <Button
          size="small"
          icon={<PlusOutlined />}
          onClick={onAdd}
          shape="circle"
        />
        {/* <Flex
          style={{ justifyContent: "space-between", padding: "0 14px" }}
          grow
        > */}

        {/* </Flex> */}
      </Flex>
    </AddRemoveButtonContainer>
  )
}

const CopyClassContactInfoModal = ({
  sessionId,
  className,
}: {
  sessionId: Uuid
  className: string
}) => {
  const { message } = App.useApp()
  const { iconColors } = useThemeContext()
  const { apiHandler } = useApiContext()
  const { currentUserId } = useUserContext()
  const {
    contactInfoByStudentId,
    studentIdsByType,
    session,
    studentsById,
    isReady,
  } = useCopyClassContactInfoData(sessionId)

  const storyTimeQuery = useMemo(
    () => ({
      queries: {
        classSessionId: {
          value: sessionId,
          comparator: COMPARATORS.EQUALS,
        },
      },
    }),
    [sessionId]
  )
  const teacherStoryTimes = useFilteredDocs<StoryTimeConstructor>(
    storyTimeAtoms,
    DOC_TYPES.STORY_TIMES,
    storyTimeQuery
  )

  const [selectedStudentIds, setSelectedStudentIds] = useState<Uuid[]>([])
  const [checkedKeys, setCheckedKeys] = useState<Key[]>([])
  const hasInitialized = useRef(false)
  const [expandedKeys, setExpandedKeys] = useState<Key[]>(DEFAULT_EXPANDED_KEYS)
  const [includePersonalContactInfo, setIncludePersonalContactInfo] =
    useState(false)
  const [attachmentFileList, setAttachmentFileList] = useState<UploadFile[]>([])
  const [subjectByFileUid, setSubjectByFileUid] = useState<
    Record<string, Uuid>
  >({})
  const [recordingIdByFileUid, setRecordingIdByFileUid] = useState<
    Record<string, Uuid>
  >({})
  const [isSendingStorytime, setIsSendingStorytime] = useState(false)
  // Paths written this visit, so a retry before the session snapshot echoes
  // back does not upload and append the same recording twice.
  const persistedStoryTimePathsRef = useRef(new Set<string>())
  const emailedStoryTimeStudentIdsRef = useRef(new Set<string>())

  const absentStudentIds = useMemo(() => {
    if (!session) return []
    return getAbsentStudentIds(session, studentsById)
  }, [session, studentsById])

  const disabledStudentIds = useMemo(() => {
    const disabled: Uuid[] = []
    Object.values(studentsById).forEach((student) => {
      const hasContactInfo = includePersonalContactInfo
        ? student.guardian1Email ||
          student.guardian2Email ||
          student.personalEmail ||
          student.guardian1Phone ||
          student.guardian2Phone ||
          student.personalPhone
        : student.guardian1Email ||
          student.guardian2Email ||
          student.guardian1Phone ||
          student.guardian2Phone
      if (!hasContactInfo) {
        disabled.push(student.id)
      }
    })
    return disabled
  }, [studentsById, includePersonalContactInfo])

  const allAvailableStudentIds = useMemo(() => {
    const allIds = uniq([
      ...studentIdsByType.students,
      ...studentIdsByType.makeups,
      ...studentIdsByType.tryouts,
      ...studentIdsByType.plannedAbsences,
      ...studentIdsByType.assistants,
      ...absentStudentIds,
    ])
    return allIds.filter((id) => !disabledStudentIds.includes(id))
  }, [studentIdsByType, absentStudentIds, disabledStudentIds])

  const selectedStudents = useMemo(() => {
    return selectedStudentIds
      .map((id) => studentsById[id])
      .filter(Boolean) as StudentConstructor[]
  }, [selectedStudentIds, studentsById])

  const namesForCopy = useMemo(() => {
    return selectedStudents.map((student) => student.name).join(", ")
  }, [selectedStudents])

  // One recipient per selected email, tagged with the student's name and sent
  // as bcc so families don't see each other's addresses. `emailsForCopy` is
  // derived from this list, so Copy Emails and Send Email always agree on the
  // selection.
  const selectedEmailRecipients = useMemo(() => {
    const recipients: EmailRecipient[] = []
    const seenEmails = new Set<string>()
    const checkedKeysStrings = checkedKeys.map(String)
    const selectedContactItems = checkedKeysStrings.filter(isContactKey)

    const addRecipient = (email: string, studentName: string) => {
      if (seenEmails.has(email)) return
      seenEmails.add(email)
      recipients.push({
        name: studentName,
        email,
        type: EMAIL_RECIPIENT_TYPE.BCC,
      })
    }

    selectedStudentIds.forEach((studentId) => {
      const student = studentsById[studentId]
      if (!student) return

      const studentContactItems = selectedContactItems.filter((item) =>
        item.startsWith(`${studentId}-`)
      )
      const studentEmailItems = studentContactItems.filter(isEmailKey)

      if (studentEmailItems.length > 0) {
        studentEmailItems.forEach((item) => {
          if (item.endsWith("-guardian1Email") && student.guardian1Email) {
            addRecipient(student.guardian1Email, student.name)
          } else if (
            item.endsWith("-guardian2Email") &&
            student.guardian2Email
          ) {
            addRecipient(student.guardian2Email, student.name)
          } else if (
            item.endsWith("-personalEmail") &&
            student.personalEmail &&
            includePersonalContactInfo
          ) {
            addRecipient(student.personalEmail, student.name)
          }
        })
      } else if (studentContactItems.length === 0) {
        const contactInfo = contactInfoByStudentId[studentId]
        if (contactInfo) {
          contactInfo.emails.forEach((email) =>
            addRecipient(email, student.name)
          )
        }
      }
    })
    return recipients
  }, [
    selectedStudentIds,
    checkedKeys,
    studentsById,
    contactInfoByStudentId,
    includePersonalContactInfo,
  ])

  const emailsForCopy = useMemo(
    () =>
      selectedEmailRecipients.map((recipient) => recipient.email).join(", "),
    [selectedEmailRecipients]
  )

  const phonesForCopy = useMemo(() => {
    const allPhones: string[] = []
    const checkedKeysStrings = checkedKeys.map(String)
    const selectedContactItems = checkedKeysStrings.filter(isContactKey)

    selectedStudentIds.forEach((studentId) => {
      const student = studentsById[studentId]
      if (!student) return

      const studentContactItems = selectedContactItems.filter((item) =>
        item.startsWith(`${studentId}-`)
      )
      const studentPhoneItems = studentContactItems.filter(isPhoneKey)

      if (studentPhoneItems.length > 0) {
        studentPhoneItems.forEach((item) => {
          if (item.endsWith("-guardian1Phone") && student.guardian1Phone) {
            allPhones.push(student.guardian1Phone)
          } else if (
            item.endsWith("-guardian2Phone") &&
            student.guardian2Phone
          ) {
            allPhones.push(student.guardian2Phone)
          } else if (
            item.endsWith("-personalPhone") &&
            student.personalPhone &&
            includePersonalContactInfo
          ) {
            allPhones.push(student.personalPhone)
          }
        })
      } else if (studentContactItems.length === 0) {
        const contactInfo = contactInfoByStudentId[studentId]
        if (contactInfo) {
          allPhones.push(...contactInfo.phones)
        }
      }
    })
    return uniq(allPhones).join(", ")
  }, [
    selectedStudentIds,
    checkedKeys,
    studentsById,
    contactInfoByStudentId,
    includePersonalContactInfo,
  ])

  const storyTimeStudents = useMemo(() => {
    const ids = uniq([
      ...studentIdsByType.students,
      ...studentIdsByType.makeups,
      ...studentIdsByType.tryouts,
      ...studentIdsByType.assistants,
      ...studentIdsByType.plannedAbsences,
      ...absentStudentIds,
    ])
    return sortBy(
      ids
        .map((id) => studentsById[id])
        .filter((student): student is StudentConstructor => student != null),
      (student) => student.name
    )
  }, [absentStudentIds, studentIdsByType, studentsById])

  const onlyStoryTimeStudentId =
    storyTimeStudents.length === 1 ? storyTimeStudents[0].id : undefined

  const studentIdForFile = useCallback(
    (uid: string): Uuid | undefined =>
      subjectByFileUid[uid] ?? onlyStoryTimeStudentId,
    [onlyStoryTimeStudentId, subjectByFileUid]
  )

  // Each email is one student. A clip shared with that student is counted
  // after download, inside the send. This gate only sees staged files.
  const largestStagedBase64Bytes = useMemo(() => {
    const sizesByStudent = new Map<string, number[]>()
    for (const file of attachmentFileList) {
      const studentId = studentIdForFile(file.uid)
      if (!studentId) continue
      const sizes = sizesByStudent.get(studentId) ?? []
      sizes.push(file.size ?? 0)
      sizesByStudent.set(studentId, sizes)
    }
    let largest = 0
    for (const sizes of sizesByStudent.values()) {
      largest = Math.max(largest, storyTimeMessageBase64Bytes(sizes))
    }
    return largest
  }, [attachmentFileList, studentIdForFile])
  const attachmentsOverLimit =
    largestStagedBase64Bytes > STORYTIME_EMAIL_BASE64_LIMIT

  const stagedFilesReady =
    attachmentFileList.length === 0 ||
    attachmentFileList.every((file) => studentIdForFile(file.uid) != null)

  const checkedKeyStrings = useMemo(
    () => checkedKeys.map(String),
    [checkedKeys]
  )

  const storyTimeSendPlans = useMemo(
    () =>
      planSharedStoryTimeSends(
        teacherStoryTimes,
        Object.values(studentsById),
        selectedStudentIds,
        checkedKeyStrings
      ),
    [checkedKeyStrings, selectedStudentIds, studentsById, teacherStoryTimes]
  )

  const stagedStudentIds = useMemo(
    () =>
      uniq(
        attachmentFileList
          .map((file) => studentIdForFile(file.uid))
          .filter((id): id is Uuid => id != null)
      ),
    [attachmentFileList, studentIdForFile]
  )

  const stagedMissingGuardian = stagedStudentIds.some((studentId) => {
    const student = studentsById[studentId]
    if (!student || !selectedStudentIds.includes(studentId)) return true
    return (
      guardianEmailsForStoryTimeSend(student, checkedKeyStrings).length === 0
    )
  })

  const canSendStorytime =
    stagedFilesReady &&
    !stagedMissingGuardian &&
    (storyTimeSendPlans.length > 0 || attachmentFileList.length > 0) &&
    !attachmentsOverLimit &&
    !isSendingStorytime

  const storyTimeStudentOptions = useMemo(
    () =>
      storyTimeStudents.map((student) => ({
        value: student.id,
        label: student.name,
      })),
    [storyTimeStudents]
  )

  const savedStoryTimeRecordings = useMemo(
    () => listStoryTimeRecordings(session?.storyTimeRecordings),
    [session?.storyTimeRecordings]
  )

  const savedStoryTimeGroups = useMemo(() => {
    const grouped = groupBy(
      savedStoryTimeRecordings,
      (recording) => recording.studentId
    )
    return sortBy(
      Object.entries(grouped).map(([studentId, recordings]) => ({
        studentId,
        studentName: studentsById[studentId]?.name ?? "Unknown student",
        recordings: sortBy(recordings, ["uploadedAt", "name"]),
      })),
      (group) => group.studentName
    )
  }, [savedStoryTimeRecordings, studentsById])

  const teacherStoryTimeGroups = useMemo(() => {
    const grouped = groupBy(
      teacherStoryTimes,
      (storyTime) => storyTime.studentId
    )
    return sortBy(
      Object.entries(grouped).map(([studentId, recordings]) => ({
        studentId,
        studentName: studentsById[studentId]?.name ?? "Unknown student",
        recordings: sortBy(
          recordings,
          (recording) => -(recording.timeCreated ?? 0)
        ),
        emailId: storyTimeSendPlans.find((plan) => plan.studentId === studentId)
          ?.storyTimeId,
      })),
      (group) => group.studentName
    )
  }, [storyTimeSendPlans, studentsById, teacherStoryTimes])

  const handleSendStorytime = useCallback(async () => {
    setIsSendingStorytime(true)
    let persistedThisSend = false
    try {
      const files = attachmentFileList.flatMap((item) => {
        const file = item.originFileObj
        if (file == null) return []
        return [{ uid: item.uid, file }]
      })

      if (files.length > 0) {
        invariant(
          session != null,
          `Class session ${sessionId} is missing — can't save the storytime recording!`
        )
        invariant(
          currentUserId != null,
          "You need to be signed in to save a storytime recording!"
        )
        const classId = session.classId
        invariant(
          classId != null && classId !== "",
          `Class session ${sessionId} has no class id — can't save the storytime recording!`
        )

        const knownPaths = new Set<string>([
          ...savedStoryTimeRecordings.map((recording) => recording.path),
          ...persistedStoryTimePathsRef.current,
        ])
        const uploadedAt = new Date().toISOString()
        const created: StoryTimeRecording[] = []

        for (const { uid, file } of files) {
          const studentId = subjectByFileUid[uid] ?? onlyStoryTimeStudentId
          invariant(
            studentId != null && studentId !== "",
            `Choose a student for ${file.name}`
          )
          const recordingId = recordingIdByFileUid[uid]
          invariant(
            recordingId != null && recordingId !== "",
            `Missing an id for ${file.name}`
          )
          const path = buildStoryTimeStoragePath(
            classId,
            sessionId,
            studentId,
            recordingId
          )
          if (knownPaths.has(path)) continue

          const url = await Firestore.getInstance().uploadBlob(file, path, {
            contentType: file.type || "application/octet-stream",
            customMetadata: {
              classId,
              sessionId,
              studentId,
              recordingId,
            },
          })
          created.push(
            buildStoryTimeRecording({
              id: recordingId,
              studentId,
              name: file.name,
              contentType: file.type || "application/octet-stream",
              size: file.size,
              path,
              url,
              uploadedAt,
              recordedBy: buildStoryTimeRecorder({ userId: currentUserId }),
            })
          )
          knownPaths.add(path)
        }

        if (created.length > 0) {
          await Firebase.getInstance().updateDocumentOrThrow(
            DOC_TYPES.CLASS_SESSIONS,
            sessionId,
            storyTimeRecordingsPatch(created)
          )
          for (const recording of created) {
            persistedStoryTimePathsRef.current.add(recording.path)
          }
          persistedThisSend = true
        }
      }

      const studentIdsToSend = uniq([
        ...storyTimeSendPlans.map((plan) => plan.studentId),
        ...files.flatMap(({ uid }) => {
          const studentId = subjectByFileUid[uid] ?? onlyStoryTimeStudentId
          return studentId ? [studentId] : []
        }),
      ])

      const sentRecipients = await emailStoryTimeStudentsOnce(
        studentIdsToSend,
        emailedStoryTimeStudentIdsRef.current,
        async (studentId) => {
          const student = studentsById[studentId]
          if (!student || !selectedStudentIds.includes(studentId)) return null
          const emails = guardianEmailsForStoryTimeSend(
            student,
            checkedKeys.map(String)
          )
          if (emails.length === 0) return null

          const attachments: EmailAttachment[] = []
          const plan = storyTimeSendPlans.find(
            (candidate) => candidate.studentId === studentId
          )
          const storyTime = plan
            ? teacherStoryTimes.find(
                (candidate) => candidate.id === plan.storyTimeId
              )
            : undefined
          if (storyTime?.url) {
            attachments.push(
              await urlToEmailAttachment(
                storyTime.url,
                storyTime.filename || "story-time.m4a"
              )
            )
          }
          for (const { uid, file } of files) {
            const fileStudentId =
              subjectByFileUid[uid] ?? onlyStoryTimeStudentId
            if (fileStudentId !== studentId) continue
            attachments.push(await fileToEmailAttachment(file))
          }
          if (attachments.length === 0) return null
          if (
            encodedAttachmentsOverLimit(
              attachments.map((attachment) => attachment.content.length)
            )
          ) {
            throw new Error(
              `${student.name}'s Story Time email is over the ${formatMb(STORYTIME_EMAIL_BASE64_LIMIT)} limit.`
            )
          }

          await apiHandler.sendStorytimeEmail({
            recipients: emails.map((email) => ({
              name: student.name,
              email,
              type: EMAIL_RECIPIENT_TYPE.BCC,
            })),
            attachments,
            className,
          })
          return emails.length
        }
      )

      if (sentRecipients === 0) {
        throw new Error(
          emailedStoryTimeStudentIdsRef.current.size > 0
            ? "No further Story Time emails were sent."
            : "Nothing to email. Share a Story Time, or choose a guardian address."
        )
      }
      emailedStoryTimeStudentIdsRef.current.clear()

      message.success(
        `Storytime email sent to ${sentRecipients} email${
          sentRecipients === 1 ? "" : "s"
        }.${persistedThisSend ? " Saved on this session." : ""}`
      )
      setAttachmentFileList([])
      setSubjectByFileUid({})
      setRecordingIdByFileUid({})
    } catch (error) {
      const detail =
        error instanceof Error
          ? error.message
          : "Failed to send storytime email"
      const alreadyEmailedNames = [
        ...emailedStoryTimeStudentIdsRef.current,
      ].map((studentId) => studentsById[studentId]?.name ?? "")
      message.error(
        storyTimeSendFailureMessage(
          alreadyEmailedNames,
          detail,
          persistedThisSend
        )
      )
    } finally {
      setIsSendingStorytime(false)
    }
  }, [
    attachmentFileList,
    apiHandler,
    checkedKeys,
    className,
    currentUserId,
    message,
    onlyStoryTimeStudentId,
    savedStoryTimeRecordings,
    selectedStudentIds,
    session,
    sessionId,
    storyTimeSendPlans,
    studentsById,
    subjectByFileUid,
    teacherStoryTimes,
    recordingIdByFileUid,
  ])

  const getStudentContactKeys = (
    studentId: Uuid,
    student: StudentConstructor
  ) => {
    const keys: string[] = []
    if (student.guardian1Email) {
      keys.push(`${studentId}-guardian1Email`)
    }
    if (student.guardian2Email) {
      keys.push(`${studentId}-guardian2Email`)
    }
    if (student.personalEmail) {
      keys.push(`${studentId}-personalEmail`)
    }
    if (student.guardian1Phone) {
      keys.push(`${studentId}-guardian1Phone`)
    }
    if (student.guardian2Phone) {
      keys.push(`${studentId}-guardian2Phone`)
    }
    if (student.personalPhone) {
      keys.push(`${studentId}-personalPhone`)
    }
    return keys
  }

  const buildStudentNode = useCallback(
    (studentId: Uuid, student: StudentConstructor, checkedKeys: Key[]) => {
      const children: Array<{
        title: string
        key: string
      }> = []

      if (student.guardian1Email) {
        children.push({
          title: `${student.guardian1Name || "Guardian 1"}: ${student.guardian1Email}`,
          key: `${studentId}-guardian1Email`,
        })
      }

      if (student.guardian2Email) {
        children.push({
          title: `${student.guardian2Name || "Guardian 2"}: ${student.guardian2Email}`,
          key: `${studentId}-guardian2Email`,
        })
      }

      if (includePersonalContactInfo && student.personalEmail) {
        children.push({
          title: `personal: ${student.personalEmail}`,
          key: `${studentId}-personalEmail`,
        })
      }

      if (student.guardian1Phone) {
        children.push({
          title: `${student.guardian1Name || "Guardian 1"}: ${student.guardian1Phone}`,
          key: `${studentId}-guardian1Phone`,
        })
      }

      if (student.guardian2Phone) {
        children.push({
          title: `${student.guardian2Name || "Guardian 2"}: ${student.guardian2Phone}`,
          key: `${studentId}-guardian2Phone`,
        })
      }

      if (includePersonalContactInfo && student.personalPhone) {
        children.push({
          title: `personal: ${student.personalPhone}`,
          key: `${studentId}-personalPhone`,
        })
      }

      const hasContactInfo = children.length > 0

      const checkedKeysStrings = checkedKeys.map(String)
      const selectedContactCount = checkedKeysStrings.filter((key) => {
        if (!key.startsWith(`${studentId}-`) || !isContactKey(key)) {
          return false
        }
        if (
          (key === `${studentId}-personalEmail` ||
            key === `${studentId}-personalPhone`) &&
          !includePersonalContactInfo
        ) {
          return false
        }
        return true
      }).length

      const hasSelectedItems = selectedContactCount > 0

      return {
        title: hasContactInfo
          ? `${student.name} (${selectedContactCount}/${children.length})`
          : `${student.name} - no contact info`,
        key: studentId,
        disabled: !hasContactInfo,
        style:
          hasContactInfo && !hasSelectedItems ? { color: "#999" } : undefined,
        children: hasContactInfo ? children : undefined,
      }
    },
    [includePersonalContactInfo]
  )

  const handleToggleChildren = useCallback(
    (studentIds: Uuid[], sectionKey: string) => {
      const expandedKeysStrings = expandedKeys.map(String)
      const isSectionExpanded = expandedKeysStrings.includes(sectionKey)
      const allStudentKeys = studentIds.map((id) => id as Key)
      const allStudentsExpanded = allStudentKeys.every((key) =>
        expandedKeysStrings.includes(String(key))
      )

      if (!isSectionExpanded) {
        // First click: just open the section header
        setExpandedKeys(uniq([...expandedKeys, sectionKey]))
      } else if (allStudentsExpanded) {
        // Collapse: remove all student keys
        setExpandedKeys(
          expandedKeys.filter((key) => !allStudentKeys.includes(key))
        )
      } else {
        // Expand: add all student keys
        setExpandedKeys(uniq([...expandedKeys, ...allStudentKeys]))
      }
    },
    [expandedKeys]
  )

  const handleToggleAllStudents = useCallback(() => {
    const expandedKeysStrings = expandedKeys.map(String)
    const sectionKeys = [
      "enrolled",
      "makeups",
      "tryouts",
      "assistants",
      "plannedAbsences",
    ]
    const allSectionsExpanded = sectionKeys.every((key) =>
      expandedKeysStrings.includes(key)
    )
    const allStudentKeys = allAvailableStudentIds
      .filter((id) => !disabledStudentIds.includes(id))
      .map((id) => id as Key)
    const allStudentsExpanded = allStudentKeys.every((key) =>
      expandedKeysStrings.includes(String(key))
    )

    if (!allSectionsExpanded) {
      // First click: open all section headers
      setExpandedKeys(uniq([...expandedKeys, ...sectionKeys]))
    } else if (allStudentsExpanded) {
      // Collapse: remove all student keys, keep section headers
      setExpandedKeys(
        expandedKeys.filter(
          (key) =>
            !allStudentKeys.includes(key) && sectionKeys.includes(String(key))
        )
      )
    } else {
      // Expand: add all student keys
      setExpandedKeys(uniq([...expandedKeys, ...allStudentKeys]))
    }
  }, [expandedKeys, allAvailableStudentIds, disabledStudentIds])

  const allStudentsExpanded = useMemo(() => {
    const expandedKeysStrings = expandedKeys.map(String)
    const sectionKeys = [
      "enrolled",
      "makeups",
      "tryouts",
      "assistants",
      "plannedAbsences",
    ]
    const allSectionsExpanded = sectionKeys.every((key) =>
      expandedKeysStrings.includes(key)
    )
    if (!allSectionsExpanded) return false
    const allStudentKeys = allAvailableStudentIds
      .filter((id) => !disabledStudentIds.includes(id))
      .map((id) => id as Key)
    return allStudentKeys.every((key) =>
      expandedKeysStrings.includes(String(key))
    )
  }, [expandedKeys, allAvailableStudentIds, disabledStudentIds])

  const treeData = useMemo(() => {
    const enrolledStudents = studentIdsByType.students
      .map((studentId) => {
        const student = studentsById[studentId]
        return student
          ? buildStudentNode(studentId, student, checkedKeys)
          : null
      })
      .filter((node): node is NonNullable<typeof node> => node !== null)

    const makeupStudents = studentIdsByType.makeups
      .map((studentId) => {
        const student = studentsById[studentId]
        return student
          ? buildStudentNode(studentId, student, checkedKeys)
          : null
      })
      .filter((node): node is NonNullable<typeof node> => node !== null)

    const tryoutStudents = studentIdsByType.tryouts
      .map((studentId) => {
        const student = studentsById[studentId]
        return student
          ? buildStudentNode(studentId, student, checkedKeys)
          : null
      })
      .filter((node): node is NonNullable<typeof node> => node !== null)

    const assistantStudents = studentIdsByType.assistants
      .map((studentId) => {
        const student = studentsById[studentId]
        return student
          ? buildStudentNode(studentId, student, checkedKeys)
          : null
      })
      .filter((node): node is NonNullable<typeof node> => node !== null)

    const plannedAbsenceStudents = studentIdsByType.plannedAbsences
      .map((studentId) => {
        const student = studentsById[studentId]
        return student
          ? buildStudentNode(studentId, student, checkedKeys)
          : null
      })
      .filter((node): node is NonNullable<typeof node> => node !== null)

    const checkedKeysStrings = checkedKeys.map(String)
    const expandedKeysStrings = expandedKeys.map(String)
    const isStudentSelected = (studentId: Uuid) => {
      return (
        checkedKeysStrings.includes(studentId) ||
        checkedKeysStrings.some(
          (key) => key.startsWith(`${studentId}-`) && isContactKey(key)
        )
      )
    }
    const enrolledSelected = enrolledStudents.filter((node) =>
      isStudentSelected(node.key as Uuid)
    ).length
    const makeupSelected = makeupStudents.filter((node) =>
      isStudentSelected(node.key as Uuid)
    ).length
    const tryoutSelected = tryoutStudents.filter((node) =>
      isStudentSelected(node.key as Uuid)
    ).length
    const assistantSelected = assistantStudents.filter((node) =>
      isStudentSelected(node.key as Uuid)
    ).length
    const plannedAbsenceSelected = plannedAbsenceStudents.filter((node) =>
      isStudentSelected(node.key as Uuid)
    ).length

    const ICONS_BY_FIELD = {
      students: <StudentsIcon />,
      tryouts: <TryoutsIcon />,
      makeups: <MakeupsIcon />,
      assistants: <UserOutlined style={{ fontSize: 10 }} />,
      plannedAbsences: <PlannedAbsencesIcon />,
    }

    const sectionKeyMap: Record<
      "students" | "tryouts" | "makeups" | "assistants" | "plannedAbsences",
      string
    > = {
      students: "enrolled",
      tryouts: "tryouts",
      makeups: "makeups",
      assistants: "assistants",
      plannedAbsences: "plannedAbsences",
    }

    const createSectionTitle = (
      label: string,
      selected: number,
      total: number,
      studentIds: Uuid[],
      type:
        | "students"
        | "tryouts"
        | "makeups"
        | "assistants"
        | "plannedAbsences"
    ) => {
      const allStudentKeys = studentIds.map((id) => id as Key)
      const allStudentsExpanded = allStudentKeys.every((key) =>
        expandedKeysStrings.includes(String(key))
      )
      const sectionKey = sectionKeyMap[type]
      const isSectionExpanded = expandedKeysStrings.includes(sectionKey)
      const iconToShow = !isSectionExpanded ? (
        <ExpandIcon />
      ) : allStudentsExpanded ? (
        <CollapseIcon />
      ) : (
        <ExpandIcon />
      )

      return (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              color: iconColors[type],
            }}
          >
            {ICONS_BY_FIELD[type]}
            <strong>
              {label} ({selected}/{total})
            </strong>
          </span>
          <Button
            onClick={(e) => {
              e.stopPropagation()
              handleToggleChildren(studentIds, sectionKey)
            }}
            style={{ cursor: "pointer", userSelect: "none" }}
            icon={iconToShow}
            type="text"
            size="small"
          />
        </span>
      )
    }

    return [
      {
        title: createSectionTitle(
          "Enrolled",
          enrolledSelected,
          enrolledStudents.length,
          studentIdsByType.students,
          "students"
        ),
        key: "enrolled",
        children: enrolledStudents,
      },
      {
        title: createSectionTitle(
          "Makeups",
          makeupSelected,
          makeupStudents.length,
          studentIdsByType.makeups,
          "makeups"
        ),
        key: "makeups",
        children: makeupStudents,
      },
      {
        title: createSectionTitle(
          "Tryouts",
          tryoutSelected,
          tryoutStudents.length,
          studentIdsByType.tryouts,
          "tryouts"
        ),
        key: "tryouts",
        children: tryoutStudents,
      },
      {
        title: createSectionTitle(
          "TAs",
          assistantSelected,
          assistantStudents.length,
          studentIdsByType.assistants,
          "assistants"
        ),
        key: "assistants",
        children: assistantStudents,
      },
      {
        title: createSectionTitle(
          "Planned Absences",
          plannedAbsenceSelected,
          plannedAbsenceStudents.length,
          studentIdsByType.plannedAbsences,
          "plannedAbsences"
        ),
        key: "plannedAbsences",
        children: plannedAbsenceStudents,
      },
    ].filter((section) => (section.children?.length ?? 0) > 0)
  }, [
    studentIdsByType,
    studentsById,
    checkedKeys,
    expandedKeys,
    handleToggleChildren,
    iconColors,
    buildStudentNode,
  ])

  const handleTreeCheck = (
    checked: Key[] | { checked: Key[]; halfChecked: Key[] }
  ) => {
    const checkedArray = Array.isArray(checked) ? checked : checked.checked
    const checkedStrings = checkedArray.map(String)
    const sectionKeys = [
      "enrolled",
      "makeups",
      "tryouts",
      "assistants",
      "plannedAbsences",
    ]
    const fullyCheckedStudentIds = checkedStrings
      .filter((v) => !sectionKeys.includes(v) && !isContactKey(v))
      .map((v) => v as Uuid)
    // Students with only some contact items checked are half-checked, so their
    // parent key is missing from the checked array — derive them from the items
    const partiallyCheckedStudentIds = checkedStrings
      .filter(isContactKey)
      .map((v) => v.split("-")[0] as Uuid)
    const studentIds = uniq([
      ...fullyCheckedStudentIds,
      ...partiallyCheckedStudentIds,
    ]).filter((id) => !disabledStudentIds.includes(id))
    setSelectedStudentIds(studentIds)
    const enabledCheckedKeys = checkedArray.filter((key) => {
      const keyString = String(key)
      if (disabledStudentIds.includes(keyString as Uuid)) return false
      if (isContactKey(keyString)) {
        const studentId = keyString.split("-")[0] as Uuid
        return !disabledStudentIds.includes(studentId)
      }
      return true
    })
    setCheckedKeys(enabledCheckedKeys)
  }

  const getKeysForStudentIds = useCallback(
    (studentIds: Uuid[]): Key[] => {
      const keys: Key[] = []
      studentIds.forEach((studentId) => {
        const student = studentsById[studentId]
        if (student) {
          keys.push(studentId)
          keys.push(...getStudentContactKeys(studentId, student))
        }
      })
      return keys
    },
    [studentsById]
  )

  const handleAddAll = () => {
    const enabledIds = allAvailableStudentIds.filter(
      (id) => !disabledStudentIds.includes(id)
    )
    setSelectedStudentIds([...enabledIds])
    setCheckedKeys(getKeysForStudentIds(enabledIds))
  }

  const handleRemoveAll = () => {
    setSelectedStudentIds([])
    setCheckedKeys([])
  }

  const handleReset = useCallback(() => {
    const enabledIds = allAvailableStudentIds.filter(
      (id) => !disabledStudentIds.includes(id)
    )
    setSelectedStudentIds([...enabledIds])
    setCheckedKeys(getKeysForStudentIds(enabledIds))
  }, [allAvailableStudentIds, disabledStudentIds, getKeysForStudentIds])

  // Initialize with all students selected when data loads
  useEffect(() => {
    if (isReady && !hasInitialized.current) {
      hasInitialized.current = true
      handleReset()
    }
  }, [isReady, handleReset])

  const handleAddByType = (
    type: keyof typeof studentIdsByType | "absent" | "exceptions"
  ) => {
    let idsToAdd: Uuid[] = []
    if (type === "exceptions") {
      idsToAdd = uniq([
        ...studentIdsByType.tryouts,
        ...studentIdsByType.makeups,
        ...studentIdsByType.assistants,
      ])
    } else {
      idsToAdd = type === "absent" ? absentStudentIds : studentIdsByType[type]
    }
    const enabledIdsToAdd = idsToAdd.filter(
      (id) => !disabledStudentIds.includes(id)
    )
    setSelectedStudentIds(uniq([...selectedStudentIds, ...enabledIdsToAdd]))
    const newKeys = getKeysForStudentIds(enabledIdsToAdd)
    setCheckedKeys(uniq([...checkedKeys, ...newKeys]) as Key[])
  }

  // const handleAddSection = (type: keyof typeof studentIdsByType) => {
  //   const idsToAdd = studentIdsByType[type].filter(
  //     (id) => !disabledStudentIds.includes(id)
  //   )
  //   setSelectedStudentIds(uniq([...selectedStudentIds, ...idsToAdd]))
  //   const newKeys = getKeysForStudentIds(idsToAdd)
  //   setCheckedKeys(uniq([...checkedKeys, ...newKeys]) as Key[])
  // }

  // const handleRemoveSection = (type: keyof typeof studentIdsByType) => {
  //   const idsToRemove = studentIdsByType[type]
  //   setSelectedStudentIds(
  //     selectedStudentIds.filter((id) => !idsToRemove.includes(id))
  //   )
  //   const keysToRemove = getKeysForStudentIds(idsToRemove)
  //   const keysToRemoveStrings = new Set(keysToRemove.map(String))
  //   setCheckedKeys(
  //     checkedKeys.filter((key) => !keysToRemoveStrings.has(String(key)))
  //   )
  // }

  if (!isReady) {
    return (
      <Flex center style={{ padding: 40 }}>
        <Spin size="large" />
      </Flex>
    )
  }

  return (
    <Flex col gap={16} style={{ padding: "8px 0" }}>
      <Space wrap>
        <Button icon={<ReloadOutlined />} onClick={handleReset}>
          Reset List
        </Button>
      </Space>

      <Flex col gap={16}>
        <Flex gap={8}>
          <AddRemoveButton
            label="All"
            onAdd={handleAddAll}
            onRemove={handleRemoveAll}
          />

          <AddRemoveButton
            label="Exceptions"
            onAdd={() => handleAddByType("exceptions")}
            onRemove={() => {
              const exceptionIds = uniq([
                ...studentIdsByType.tryouts,
                ...studentIdsByType.makeups,
                ...studentIdsByType.assistants,
              ])
              setSelectedStudentIds(
                selectedStudentIds.filter((id) => !exceptionIds.includes(id))
              )
              const keysToRemove = getKeysForStudentIds(exceptionIds)
              const keysToRemoveStrings = new Set(keysToRemove.map(String))
              setCheckedKeys(
                checkedKeys.filter(
                  (key) => !keysToRemoveStrings.has(String(key))
                )
              )
            }}
          />
          {/* <Divider type="vertical" style={{ height: "60px" }} />
          <AddRemoveButton
            label="Enrolled"
            onAdd={() => handleAddSection("students")}
            onRemove={() => handleRemoveSection("students")}
            icon={<StudentsIcon />}
            color={iconColors.students}
          />
          <AddRemoveButton
            label="Makeups"
            onAdd={() => handleAddSection("makeups")}
            onRemove={() => handleRemoveSection("makeups")}
            icon={<MakeupsIcon />}
            color={iconColors.makeups}
          />
          <AddRemoveButton
            label="Tryouts"
            onAdd={() => handleAddSection("tryouts")}
            onRemove={() => handleRemoveSection("tryouts")}
            icon={<TryoutsIcon />}
            color={iconColors.tryouts}
          />

          <AddRemoveButton
            label="TAs"
            onAdd={() => handleAddSection("assistants")}
            onRemove={() => handleRemoveSection("assistants")}
            icon={<UserOutlined style={{ fontSize: 10 }} />}
            color={iconColors.assistants}
          />
          <AddRemoveButton
            label="Planned"
            onAdd={() => handleAddSection("plannedAbsences")}
            onRemove={() => handleRemoveSection("plannedAbsences")}
            icon={<PlannedAbsencesIcon />}
            color={iconColors.plannedAbsences}
          /> */}
        </Flex>
      </Flex>

      <Checkbox
        checked={includePersonalContactInfo}
        onChange={(e) => setIncludePersonalContactInfo(e.target.checked)}
      >
        Include student personal contact info
      </Checkbox>

      <Flex col gap={8}>
        <Flex style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div>Selected Students ({selectedStudentIds.length}):</div>
          <Button
            onClick={(e) => {
              e.stopPropagation()
              handleToggleAllStudents()
            }}
            style={{ cursor: "pointer", userSelect: "none" }}
            icon={allStudentsExpanded ? <CollapseIcon /> : <ExpandIcon />}
            type="text"
            size="small"
          />
        </Flex>
        <Tree
          checkable
          treeData={treeData}
          checkedKeys={checkedKeys}
          onCheck={handleTreeCheck}
          expandedKeys={expandedKeys}
          onExpand={(keys) => setExpandedKeys(keys)}
          style={{ width: "100%", maxHeight: 450, overflowY: "auto" }}
        />
      </Flex>

      <Space>
        <Button
          icon={<UserOutlined />}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(namesForCopy)
              message.success("Names copied to clipboard")
            } catch {
              message.error("Failed to copy names")
            }
          }}
        >
          Copy Names
        </Button>
        <Button
          icon={<MailOutlined />}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(emailsForCopy)
              message.success("Emails copied to clipboard")
            } catch {
              message.error("Failed to copy emails")
            }
          }}
        >
          Copy Emails
        </Button>
        <Button
          icon={<PhoneOutlined />}
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(phonesForCopy)
              message.success("Phone numbers copied to clipboard")
            } catch {
              message.error("Failed to copy phone numbers")
            }
          }}
        >
          Copy Phone Numbers
        </Button>
      </Space>

      <Divider style={{ margin: "4px 0" }} />

      <Flex col gap={8}>
        <div style={{ fontWeight: 500 }}>Storytime Email</div>
        {teacherStoryTimeGroups.length > 0 && (
          <div data-testid="storytime-teacher-recordings">
            <div style={{ fontWeight: 500, marginBottom: 4 }}>
              Teacher recordings
            </div>
            {teacherStoryTimeGroups.map((group) => (
              <div
                key={group.studentId}
                data-testid="storytime-teacher-student"
                data-student-id={group.studentId}
                style={{ marginBottom: 8 }}
              >
                <div style={{ fontWeight: 500 }}>{group.studentName}</div>
                {group.recordings.map((recording) => {
                  const shared = isStoryTimeShared(recording)
                  const willEmail = recording.id === group.emailId
                  return (
                    <div
                      key={recording.id}
                      data-testid="storytime-teacher-recording"
                      data-shared={shared ? "true" : "false"}
                      data-will-email={willEmail ? "true" : "false"}
                      style={{ marginBottom: 8 }}
                    >
                      <div>{recording.filename || "Story Time"}</div>
                      <audio
                        controls
                        preload="none"
                        src={recording.url}
                        style={{
                          display: "block",
                          width: "100%",
                          marginTop: 4,
                        }}
                      />
                      <div style={{ color: "#999", fontSize: 12 }}>
                        {willEmail
                          ? "Shared — emails this student's guardians"
                          : shared
                            ? "Shared — an older recording; the newest shared one is emailed"
                            : "Not shared — share it on Standout Review before emailing"}
                      </div>
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        )}
        {savedStoryTimeGroups.length > 0 && (
          <div data-testid="storytime-saved-recordings">
            <div style={{ fontWeight: 500, marginBottom: 4 }}>
              Saved on this session
            </div>
            {savedStoryTimeGroups.map((group) => (
              <div
                key={group.studentId}
                data-testid="storytime-saved-student"
                data-student-id={group.studentId}
                style={{ marginBottom: 8 }}
              >
                <div style={{ fontWeight: 500 }}>{group.studentName}</div>
                {group.recordings.map((recording) => (
                  <div
                    key={recording.id}
                    data-testid="storytime-saved-recording"
                    style={{ marginBottom: 8 }}
                  >
                    <a href={recording.url} target="_blank" rel="noreferrer">
                      {recording.name}
                    </a>
                    {recording.contentType.startsWith("audio/") && (
                      <audio
                        controls
                        preload="none"
                        src={recording.url}
                        style={{
                          display: "block",
                          width: "100%",
                          marginTop: 4,
                        }}
                      />
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
        {/* Staged files stay local until Send. Each file is assigned to one
            student, uploaded under that student, and written onto the session
            map. The same bytes are then base64-encoded into the email.
            Deliberately NO `accept` filter: macOS can stall the native file
            dialog while resolving accept patterns against its file-type
            database, which reads as "clicking does nothing" — and any
            attachment type is fine. */}
        <Upload.Dragger
          multiple
          fileList={attachmentFileList}
          beforeUpload={() => false}
          onChange={({ fileList }) => {
            setAttachmentFileList(fileList)
            const uids = new Set(fileList.map((file) => file.uid))
            setSubjectByFileUid((current) => {
              const next: Record<string, Uuid> = {}
              for (const [uid, studentId] of Object.entries(current)) {
                if (uids.has(uid)) next[uid] = studentId
              }
              return next
            })
            // getNewRef only allocates a client id. It does not write a doc.
            // Minted once per staged file so a failed-email retry overwrites
            // the same map entry instead of adding another.
            setRecordingIdByFileUid((current) => {
              const next: Record<string, Uuid> = {}
              for (const file of fileList) {
                next[file.uid] =
                  current[file.uid] ??
                  Firebase.getInstance().getNewRef(DOC_TYPES.CLASS_SESSIONS).id
              }
              return next
            })
          }}
          itemRender={(originNode, file) => (
            <Flex gap={8} style={{ alignItems: "center", width: "100%" }}>
              <div style={{ flex: 1, minWidth: 0 }}>{originNode}</div>
              <Select
                data-testid="storytime-recording-student"
                aria-label={`Student for ${file.name}`}
                placeholder="Which student?"
                value={studentIdForFile(file.uid)}
                options={storyTimeStudentOptions}
                onChange={(studentId: Uuid) => {
                  setSubjectByFileUid((current) => ({
                    ...current,
                    [file.uid]: studentId,
                  }))
                }}
                showSearch
                optionFilterProp="label"
                popupMatchSelectWidth={false}
                style={{ width: 200 }}
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
              />
            </Flex>
          )}
        >
          <p className="ant-upload-drag-icon">
            <PaperClipOutlined />
          </p>
          <p>Click or drag files here to attach</p>
        </Upload.Dragger>
        {attachmentsOverLimit && (
          <div style={{ color: "red" }}>
            Attachments are too large to email (
            {formatMb(largestStagedBase64Bytes)} encoded for one student — the
            limit is {formatMb(STORYTIME_EMAIL_BASE64_LIMIT)}). Remove some
            files.
          </div>
        )}
        <Flex gap={8} style={{ alignItems: "center" }}>
          <Popconfirm
            title="Send storytime email?"
            description="Each student's recording goes only to that student's guardian addresses (BCC)."
            onConfirm={handleSendStorytime}
            okText="Send"
            disabled={!canSendStorytime}
          >
            <Button
              type="primary"
              icon={<SendOutlined />}
              loading={isSendingStorytime}
              disabled={!canSendStorytime}
              data-testid="storytime-send-button"
            >
              Send Email
            </Button>
          </Popconfirm>
          <span style={{ color: "#999" }}>
            {!stagedFilesReady
              ? "Choose a student for each recording"
              : stagedMissingGuardian
                ? "Choose a guardian address for each recording"
                : storyTimeSendPlans.length === 0 &&
                    attachmentFileList.length === 0
                  ? teacherStoryTimes.length > 0
                    ? "Share a Story Time on Standout Review before emailing"
                    : "Attach at least one file"
                  : "Sends each student's recording only to that student's guardians"}
          </span>
        </Flex>
      </Flex>
    </Flex>
  )
}

const CopyClassContactInfo = ({
  sessionId,
  className,
}: {
  sessionId: Uuid
  className: string
}) => {
  const canSeeContactInfo = useHasPermission(
    RESOURCES.STUDENT,
    USER_ACTIONS.VIEW_CONTACT_INFO
  )

  if (!canSeeContactInfo) {
    return null
  }

  return (
    <Flex style={{ backgroundColor: "white", borderRadius: "50%" }}>
      <ButtonToggleModal
        buttonLabel={<MailOutlined />}
        tooltip="Copy class contact info"
        modalTitle={`Build Email List - ${className || "Unknown Class"}`}
        isIcon
        width={800}
        size="small"
      >
        <CopyClassContactInfoModal
          sessionId={sessionId}
          className={className}
        />
      </ButtonToggleModal>
    </Flex>
  )
}

export default CopyClassContactInfo
